/**
 * Parsley Health — PostHog Analytics
 *
 * Tracks key e-commerce events:
 *   - Customer identification (logged-in users)
 *   - product_viewed
 *   - collection_viewed
 *   - cart_viewed
 *   - add_to_cart        (intercepts fetch to /cart/add.js)
 *   - checkout_started   (checkout button click)
 *   - search_performed   (search form submit)
 *
 * Purchase / order_completed tracking lives in Shopify Admin:
 *   Settings → Checkout → Order status page additional scripts
 *   (see posthog-order-tracking.liquid for the snippet to paste there)
 *
 * Data is provided by window.__posthogData, injected via Liquid in theme.liquid.
 */

(function () {
  'use strict';

  // PostHog must be initialized before this file runs
  if (typeof window.posthog === 'undefined') {
    console.warn('[PostHog] posthog not found — analytics disabled.');
    return;
  }

  var data = window.__posthogData || {};

  // ─── Customer Identification ─────────────────────────────────────────────────

  function identifyCustomer() {
    var customer = data.customer;
    if (!customer || !customer.id) return;

    posthog.identify(String(customer.id), {
      email: customer.email,
      first_name: customer.firstName,
      last_name: customer.lastName,
      name: (customer.firstName + ' ' + customer.lastName).trim(),
      orders_count: customer.ordersCount,
      total_spent: customer.totalSpent,
    });
  }

  // ─── Product Viewed ──────────────────────────────────────────────────────────

  function trackProductViewed() {
    if (data.pageType !== 'product' || !data.product) return;

    var p = data.product;
    posthog.capture('product_viewed', {
      product_id: p.id,
      product_title: p.title,
      product_type: p.type,
      product_vendor: p.vendor,
      product_url: p.url,
      price: p.price,
      compare_at_price: p.compareAtPrice,
      variant_id: p.selectedVariantId,
      variant_title: p.selectedVariantTitle,
      sku: p.selectedVariantSku,
    });
  }

  // ─── Collection Viewed ───────────────────────────────────────────────────────

  function trackCollectionViewed() {
    if (data.pageType !== 'collection' || !data.collection) return;

    posthog.capture('collection_viewed', {
      collection_id: data.collection.id,
      collection_title: data.collection.title,
      collection_handle: data.collection.handle,
    });
  }

  // ─── Cart Viewed ─────────────────────────────────────────────────────────────

  function trackCartViewed() {
    if (data.pageType !== 'cart') return;
    posthog.capture('cart_viewed', {
      cart_total: data.cartTotal,
    });
  }

  // ─── Add to Cart ─────────────────────────────────────────────────────────────
  // Intercepts fetch calls to /cart/add.js so we capture regardless of
  // which theme component (product form, quick-buy, cart drawer) triggers it.

  function interceptAddToCart() {
    var originalFetch = window.fetch;

    window.fetch = function (url, options) {
      var promise = originalFetch.apply(this, arguments);

      var urlStr = (typeof url === 'string') ? url : (url && url.url) || '';
      if (!urlStr.includes('/cart/add')) return promise;

      promise.then(function (response) {
        if (!response.ok) return;

        response.clone().json().then(function (body) {
          // Shopify returns different shapes depending on whether
          // the request used the `sections` param or not.
          var items = [];
          if (body.items_added) {
            items = body.items_added;
          } else if (Array.isArray(body.items)) {
            items = body.items;
          } else if (body.variant_id) {
            items = [body];
          }

          items.forEach(function (item) {
            if (!item || !item.variant_id) return;

            posthog.capture('add_to_cart', {
              product_id: item.product_id,
              variant_id: item.variant_id,
              product_title: item.product_title || item.title,
              variant_title: item.variant_title,
              price: (item.price / 100).toFixed(2),
              quantity: item.quantity,
              sku: item.sku,
              vendor: item.vendor,
            });
          });
        }).catch(function () {});
      }).catch(function () {});

      return promise;
    };
  }

  // ─── Checkout Started ────────────────────────────────────────────────────────
  // Uses form submit (not click) so the event fires reliably before Shopify
  // navigates away to the hosted checkout page.

  function trackCheckoutStarted() {
    // Cart form submissions — covers both the cart page and cart drawer
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;

      var hasCheckoutButton = !!form.querySelector('[name="checkout"]');
      var actionIsCart = (form.getAttribute('action') || '').includes('/cart');
      if (!hasCheckoutButton && !actionIsCart) return;

      posthog.capture('checkout_started', { cart_total: data.cartTotal });
    });

    // Express checkout / "Buy it now" links that go directly to /checkout
    document.addEventListener('click', function (event) {
      var target = event.target;
      while (target && target !== document.body) {
        if (target.tagName === 'A' && target.href && target.href.includes('/checkout')) {
          posthog.capture('checkout_started', { cart_total: data.cartTotal });
          break;
        }
        target = target.parentElement;
      }
    }, { passive: true });
  }

  // ─── Search Performed ────────────────────────────────────────────────────────

  function trackSearch() {
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;

      var action = form.getAttribute('action') || '';
      if (!action.includes('/search') && form.getAttribute('role') !== 'search') return;

      var input = form.querySelector('[name="q"]');
      var query = input ? input.value.trim() : '';
      if (!query) return;

      posthog.capture('search_performed', { query: query });
    }, { passive: true });
  }

  // ─── Init ────────────────────────────────────────────────────────────────────

  function init() {
    identifyCustomer();
    trackProductViewed();
    trackCollectionViewed();
    trackCartViewed();
    interceptAddToCart();
    trackCheckoutStarted();
    trackSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
